import { useCallback, useEffect, useState } from "react";
import { galerija } from "@/mocks/galerija";
import ImageSlot from "@/components/base/ImageSlot";

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = useCallback(() => setActiveIndex(null), []);

  const next = useCallback(() => {
    setActiveIndex((current) =>
      current === null ? current : (current + 1) % galerija.length,
    );
  }, []);

  const prev = useCallback(() => {
    setActiveIndex((current) =>
      current === null ? current : (current - 1 + galerija.length) % galerija.length,
    );
  }, []);

  useEffect(() => {
    if (activeIndex === null) return;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [activeIndex, close, next, prev]);

  const active = activeIndex === null ? null : galerija[activeIndex];

  return (
    <section id="galerija" className="scroll-mt-24 bg-background-100 py-16 md:py-24 lg:py-28">
      <div className="section-shell">
        <div className="reveal flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-4">
              <span className="font-label text-[10px] font-semibold tracking-[0.2em] text-accent-700">
                03
              </span>
              <span className="rule-line" />
              <span className="eyebrow text-secondary-600">Galerija ambienta</span>
            </div>
            <h2 className="headline mt-6 max-w-[520px] text-[31px] text-foreground-950 sm:text-[39px] lg:text-[48px]">
              Vzdušje, ki ga
              <span className="block italic text-secondary-600">začutite ob vstopu.</span>
            </h2>
          </div>
          <div className="md:max-w-[320px] md:text-right">
            <p className="text-[14px] leading-relaxed text-foreground-600">
              Topla svetloba, lesene mize in prostor, kjer se ustavi čas —
              poglejte si kotičke, ki vas čakajo.
            </p>
            <p className="mt-4 flex items-center gap-2 font-label text-[9px] uppercase tracking-[0.2em] text-foreground-400 md:justify-end">
              <i className="ri-expand-diagonal-line text-[13px] text-accent-600" />
              Kliknite fotografijo za večji prikaz
            </p>
          </div>
        </div>

        <div className="reveal mt-10 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[190px] md:auto-rows-[220px] md:gap-4 lg:auto-rows-[230px] lg:grid-cols-4">
          {galerija.map((foto, index) => {
            const hasImage = Boolean(foto.slika);

            return (
              <div
                key={foto.id}
                className={`group relative overflow-hidden rounded-lg border border-background-200 bg-background-200/50 text-left transition-colors duration-500 hover:border-accent-400 ${
                  foto.span === "col-span-2 row-span-2"
                    ? "col-span-2 row-span-2"
                    : foto.span === "col-span-2 row-span-1"
                      ? "col-span-2 row-span-1"
                      : "col-span-1 row-span-1"
                }`}
              >
                {hasImage ? (
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Odpri fotografijo: ${foto.naslov}`}
                    className="absolute inset-0 h-full w-full cursor-pointer"
                  >
                    <img
                      src={foto.slika}
                      alt={foto.alt}
                      title={`${foto.naslov} – Bar Stanka Ptuj`}
                      className="h-full w-full object-cover object-center transition-transform duration-[1300ms] ease-smooth group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-95" />
                    <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-background-50/35 bg-primary-950/30 text-background-50 opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:opacity-100">
                      <i className="ri-expand-diagonal-line text-sm" />
                    </span>
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4 text-left md:p-5">
                      <div>
                        <p className="font-heading text-lg text-background-50 md:text-xl">
                          {foto.naslov}
                        </p>
                        <p className="mt-1 hidden text-[12px] text-background-100/75 sm:block">
                          {foto.opis}
                        </p>
                      </div>
                      <span className="hidden shrink-0 font-label text-[9px] tracking-[0.18em] text-background-100/55 sm:block">
                        0{index + 1}
                      </span>
                    </div>
                  </button>
                ) : (
                  <>
                    <ImageSlot
                      tone="light"
                      icon="ri-image-add-line"
                      label="Prostor za fotografijo"
                      hint={foto.naslov}
                    />
                    <span className="absolute left-4 top-4 flex h-7 min-w-7 items-center justify-center rounded-full bg-background-50/90 px-2 font-label text-[10px] font-bold tracking-[0.08em] text-primary-900">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {active?.slika && (
        <div
          className="fixed inset-0 z-[80] flex flex-col bg-primary-950/95 px-4 pb-[max(20px,env(safe-area-inset-bottom))] pt-5 backdrop-blur-sm md:px-8 md:py-5"
          role="dialog"
          aria-modal="true"
          aria-label={active.naslov}
        >
          <div className="flex items-center justify-between text-background-100/80">
            <span className="font-label text-[10px] uppercase tracking-[0.24em]">
              {String((activeIndex ?? 0) + 1).padStart(2, "0")} /{" "}
              {String(galerija.length).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={close}
              aria-label="Zapri prikaz"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/30 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-950"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center py-4">
            <div className="relative max-h-full w-full max-w-[1080px]">
              <img
                src={active.slika}
                alt={active.alt}
                title={`${active.naslov} – Bar Stanka Ptuj`}
                className="mx-auto max-h-[72vh] w-auto max-w-full rounded-lg object-contain"
              />

              <button
                type="button"
                onClick={prev}
                aria-label="Prejšnja fotografija"
                className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-100/30 bg-primary-950/50 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-950 md:-left-4"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Naslednja fotografija"
                className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-100/30 bg-primary-950/50 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-950 md:-right-4"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>
          </div>

          <div className="text-center">
            <p className="font-heading text-xl text-background-50">{active.naslov}</p>
            <p className="mt-1 text-[13px] text-background-100/60">{active.opis}</p>
          </div>
        </div>
      )}
    </section>
  );
}